import React, { useState } from "react";
import { Button, Input } from "semantic-ui-react";

interface Props {
  selected: string[];
  options?: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Hide the suggestion pool and collect only the learner's own words. */
  customOnly?: boolean;
  disabled?: boolean;
}

/**
 * The pill picker shared by five of the DTLYL challenges.
 *
 * A pool of suggested words rendered as clickable pills, the learner's
 * selections rendered as removable pills, and a free-text field to add their
 * own. Selected words are removed from the pool rather than shown twice, which
 * is how the original behaved.
 *
 * Pure and controlled — it never touches the API. The dispatcher owns saving.
 */
const PillPicker: React.FC<Props> = ({
  selected,
  options = [],
  onChange,
  placeholder = "Add your own",
  customOnly = false,
  disabled = false,
}) => {
  const [draft, setDraft] = useState("");

  const chosen = selected || [];
  const remaining = (options || []).filter((o) => !chosen.includes(o));

  const add = (word: string) => {
    const trimmed = (word || "").trim();
    if (!trimmed || chosen.includes(trimmed)) return;
    onChange([...chosen, trimmed]);
  };

  const remove = (word: string) => onChange(chosen.filter((w) => w !== word));

  const addDraft = () => {
    add(draft);
    setDraft("");
  };

  return (
    <div className="pill-picker">
      <div className="pill-picker-selected">
        {chosen.map((word) => (
          <button
            type="button"
            key={word}
            className="ui large orange circular label selection dtlyl-pill"
            onClick={() => remove(word)}
            disabled={disabled}
            aria-label={`Remove ${word}`}
          >
            {word}
          </button>
        ))}
      </div>

      {!disabled && (
        <Input
          className="custom-quadrant-control"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addDraft();
            }
          }}
          action={
            <Button
              icon="plus"
              basic
              circular
              color="blue"
              size="small"
              onClick={addDraft}
              type="button"
            />
          }
        />
      )}

      {!customOnly && !disabled && (
        <div className="pill-picker-options">
          {remaining.map((word) => (
            <button
              type="button"
              key={word}
              className="ui basic large white circular label selection dtlyl-pill"
              onClick={() => add(word)}
              aria-label={`Add ${word}`}
            >
              {word}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PillPicker;
